/**
 * Loading the signed-in account (GET /api/auth/me) at app start.
 *
 * Only a real auth rejection (401/403) means "signed out". A 429, 5xx,
 * timeout or network error is transient: retry with backoff, honour
 * Retry-After, and if it still fails show an explicit "couldn't load your
 * account" state with a retry button. Never treat it as a logout (that left
 * a blank page on Testing when the seeded account hit its hourly limit).
 */

import { analyticsErrorCode } from '../services/analyticsPrivacy';

export type AuthFailureKind = 'unauthenticated' | 'retryable';

type HttpLikeError = {
  response?: { status?: number; headers?: Record<string, unknown>; data?: unknown };
};

/** Parses a Retry-After header (seconds or HTTP date); null when absent or invalid. */
export function parseRetryAfterMs(headerValue: unknown, now: number = Date.now()): number | null {
  const raw = Array.isArray(headerValue) ? headerValue[0] : headerValue;
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  const seconds = Number(raw);
  if (Number.isFinite(seconds)) return Math.max(seconds * 1000, 0);
  const dateValue = Date.parse(raw);
  if (!Number.isNaN(dateValue)) return Math.max(dateValue - now, 0);
  return null;
}

function statusOf(error: unknown): number | undefined {
  return (error as HttpLikeError | null)?.response?.status;
}

export function classifyAuthFailure(error: unknown): AuthFailureKind {
  const status = statusOf(error);
  // No response at all: network down, CORS blip, timeout.
  if (status === undefined) return 'retryable';
  if (status === 408 || status === 429 || status >= 500) return 'retryable';
  return 'unauthenticated';
}

export const AUTH_LOAD_MAX_ATTEMPTS = 3;
export const AUTH_LOAD_BASE_DELAY_MS = 1_000;
export const AUTH_LOAD_MAX_DELAY_MS = 8_000;
/** A longer Retry-After isn't waited out behind a spinner: show the error state instead. */
export const AUTH_LOAD_MAX_RETRY_AFTER_MS = 10_000;

/**
 * The wait the server asked for: the Retry-After header, else the 429 body's
 * `retry_after_seconds` (the header is unreadable cross-origin unless exposed).
 */
export function serverRetryAfterMs(error: unknown): number | null {
  const response = (error as HttpLikeError | null)?.response;
  const fromHeader = parseRetryAfterMs(response?.headers?.['retry-after']);
  if (fromHeader !== null) return fromHeader;
  const seconds = (response?.data as { retry_after_seconds?: unknown } | undefined)?.retry_after_seconds;
  return typeof seconds === 'number' && Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : null;
}

/** Delay before retry number `attempt` (0-based): the server's Retry-After when given, else capped exponential backoff. */
export function authRetryDelayMs(error: unknown, attempt: number): number {
  return serverRetryAfterMs(error) ?? Math.min(AUTH_LOAD_BASE_DELAY_MS * 2 ** attempt, AUTH_LOAD_MAX_DELAY_MS);
}

/** Thrown when the account could not be loaded for a transient reason. */
export class AccountUnavailableError extends Error {
  readonly retryAfterMs: number | null;
  readonly original: unknown;

  constructor(original: unknown, retryAfterMs: number | null) {
    super('account_unavailable');
    this.name = 'AccountUnavailableError';
    this.original = original;
    this.retryAfterMs = retryAfterMs;
  }
}

export function isAccountUnavailable(error: unknown): error is AccountUnavailableError {
  return error instanceof AccountUnavailableError;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Runs `load`, retrying transient failures. Rethrows auth rejections as-is
 * (the caller signs the user out); throws AccountUnavailableError when a
 * transient failure persists or the server asks to wait longer than we will.
 */
export async function loadAccountWithRetry<T>(
  load: () => Promise<T>,
  options: { maxAttempts?: number; sleep?: (ms: number) => Promise<void> } = {},
): Promise<T> {
  const maxAttempts = options.maxAttempts ?? AUTH_LOAD_MAX_ATTEMPTS;
  const sleep = options.sleep ?? defaultSleep;
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await load();
    } catch (error) {
      if (classifyAuthFailure(error) === 'unauthenticated') throw error;
      const delay = authRetryDelayMs(error, attempt);
      if (attempt + 1 >= maxAttempts || delay > AUTH_LOAD_MAX_RETRY_AFTER_MS) {
        // Only a server-given wait schedules the automatic retry; otherwise the user retries.
        throw new AccountUnavailableError(error, serverRetryAfterMs(error));
      }
      await sleep(delay);
    }
  }
}

/**
 * Properties for `app_account_load_retry_shown`: why the account could not be
 * loaded (a stable code such as `http_429`, never the error text) and whether
 * the server told us when to retry (the screen then retries on its own).
 */
export function accountUnavailableEventProperties(error: AccountUnavailableError): {
  error_code: string;
  has_retry_after: boolean;
  retry_after_ms: number | null;
} {
  return {
    error_code: analyticsErrorCode(error.original),
    has_retry_after: error.retryAfterMs !== null,
    retry_after_ms: error.retryAfterMs,
  };
}

